/**
 * LeavesView
 *
 * Congés: the leaves of the selected teams, in the same MUI DataGrid layout as the arrêts
 * suivi (LeavesDataTable), with the requests awaiting an answer above it. A phone gets the
 * card list (MobileLeavesView) instead of the grid.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import {
    isOwner,
    showErrorToast,
    getDayPeriodFromDate,
    checkPermission
} from "../../app/utils/helpers";
import ExcelManager from "../../app/managers/excelManager";
import addIcon from "../../assets/images/add.png";
import addIconFocus from "../../assets/images/addFocus.png";
import exportIcon from "../../assets/images/export.png";
import exportIconFocus from "../../assets/images/exportFocus.png";
import ImageButton from "../../components/buttons/ImageButton";
import { LeaveFormModal } from "./LeaveFormModal";
import {
    getLeaves,
    hideLeaveModal,
    requestDeleteLeave,
    setLeaveStatus,
    showLeaveModal,
    submitLeave,
    showAdjustmentModal,
    hideAdjustmentModal
} from "./leavesSlice";
import { LeaveAdjustmentsModal } from "./LeaveAdjustmentsModal";
import { ConfirmDeleteModal } from '../../components/popups/ConfirmDeleteModal';
import { TeamSelector } from '../../components/teams/TeamSelector';
import { ConfirmModal } from '../../components/popups/ConfirmModal';
import MobileLeavesView from './mobile/MobileLeavesView';
import { MobileLeaveFormDrawer } from './mobile/MobileLeaveFormDrawer';
import MobileLeavesTopMenu from './mobile/MobileLeavesTopMenu';
import { getCollabs } from '../../components/collabs/collabsSlice';
import { setSelectedTeams } from '../../components/teams/teamsSlice';
import { config } from '../../app/utils/config';
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LeavePendingRequests from '../../components/leaves/LeavePendingRequests';
import LeavesDataTable from './LeavesDataTable';

export function LeavesView() {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const dtLeavesApi = useGridApiRef()

    const userId = useSelector((state) => state.login.userId)
    const leaves2 = useSelector((state) => state.leaves.list)
    const leaveModalType = useSelector((state) => state.leaves.leaveModalType)
    const isMobile = useSelector((state) => state.settings.isMobile)
    const lang = useSelector((state) => state.settings.lang)
    const collabs = useSelector((state) => state.collabs.currentList)
    const adjustmentModalOpen = useSelector((state) => state.leaves.adjustmentModalOpen)
    const sampleLeaves = useSelector((state) => state.sampleLeaves.list)

    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds)
    const myTeam = useSelector((state) => state.teams.myTeam)
    const userRoleId = useSelector((state) => state.login.userRoleId)
    const [showDeletePopup, setShowDeletePopup] = useState(false)
    const [showConfirmPopup, setShowConfirmPopup] = useState(false)
    const [selectedLeave, setSelectedLeave] = useState({})

    const viewingOwnTeam = myTeam && selectedTeamsIds?.some(id => id == myTeam.id)
    const hasEditOnViewedTeams = checkPermission(config.scopes.leaves, config.permissionTypes.edit, selectedTeamsIds)
    const canRequestLeave = viewingOwnTeam || hasEditOnViewedTeams

    useEffect(() => {
        if (selectedTeamsIds && selectedTeamsIds.length) {
            dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }))
        }
    }, [dispatch, selectedTeamsIds])

    useEffect(() => {
        loadLeaves()
    }, [dispatch, collabs])

    function loadLeaves() {
        if (selectedTeamsIds && selectedTeamsIds.length && collabs.length) {
            let selectedCollabsIds = collabs.reduce((array, current) => {
                array.push(current.id)
                return array
            }, [])
            dispatch(getLeaves({ selectedTeamsIds, selectedCollabsIds }))
        }
    }

    /** The loaded leaves as an Excel sheet, one line per leave, columns as the grid shows them. */
    const exportExcel = () => {
        const L = (key) => Localize(key, lang)
        const bound = (value) => value ? `${moment(value).format("DD/MM/YYYY")} ${getDayPeriodFromDate(value) || ""}`.trim() : ""
        const typeLabels = (sampleLeaves || []).reduce((map, sample) => {
            if (!map[sample.type]) map[sample.type] = sample.label
            return map
        }, {})
        const rows = (leaves2 || []).map(leave => ({
            [L("Matricule")]: leave.id_silae || "",
            [L("Company")]: leave.company || "",
            [L("Family Name")]: leave.nom || "",
            [L("Name")]: leave.prenom || "",
            [L("WORK_STOPPAGE_CURRENT_TEAM")]: leave.currentTeamName || "",
            [L("LEAVE_TEAM")]: leave.teamName || "",
            [L("Type")]: typeLabels[leave.type] || leave.type || "",
            [L("Status")]: leave.status ? L(`LEAVE_STATUS_${leave.status}`) : "",
            [L("Start")]: bound(leave.fromDate),
            [L("End")]: bound(leave.toDate),
            [L("Duration")]: leave.duration || "",
            [L("CREATED_AT")]: leave.createdAtTime ? moment(leave.createdAtTime).format("DD/MM/YYYY HH:mm") : "",
            [L("Validated by")]: leave.validatedBy || "",
            [L("Validated at")]: leave.validatedAt ? moment(leave.validatedAt).format("DD/MM/YYYY HH:mm") : "",
        }))
        ExcelManager.getInstance().generateAndDownloadExcelFromData(rows, "conges")
    }

    const openNew = () => {
        dispatch(showLeaveModal({ type: "ADD_LEAVE" }))
    }

    function updateLeave(id, status) {
        let data = {
            id: id,
            status: status
        }

        dispatch(submitLeave(data)).then((response) => {
            if (response['payload']['responseData']) {
                dispatch(setLeaveStatus(data))
                if (["DELETED", "REJECTED"].includes(status)) {
                    dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }))
                    loadLeaves()
                }
            }
            if (response['payload']['responseError']) {
                if (response['payload']['responseError']['data'] && response['payload']['responseError']['data']['message']) {
                    showErrorToast(response['payload']['responseError']['data']['message'])
                } else showErrorToast()
            }
        })
    }

    function handleDelete(rowData) {
        setSelectedLeave(rowData)
        setShowDeletePopup(true)
    }

    function handleRequestDelete(rowData) {
        setSelectedLeave(rowData)
        setShowConfirmPopup(true)
    }

    const handleRequestDeleteSelectedLeave = async () => {
        setShowConfirmPopup(false)
        dispatch(requestDeleteLeave({ id: selectedLeave.id })).then((response) => {
            if (response['payload']['responseData']) {
                dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }))
                loadLeaves()
            }
            if (response['payload']['responseError']) {
                showErrorToast()
            }
        })
    }

    const handleDeleteSelectedLeave = async () => {
        setShowDeletePopup(false)
        updateLeave(selectedLeave.id, "DELETED")
    }

    function isManager(teamId) {
        return checkPermission(config.scopes.leaves, config.permissionTypes.edit, teamId)
    }

    const canManage = useCallback(
        (leave) => isOwner(userRoleId) || isManager(leave.id_team),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userRoleId, selectedTeamsIds]
    )

    /**
     * The planning, weekly view, starting on the leave's first day, on the team the leave was
     * taken in — the same jump as the arrêts suivi's (WorkStoppagesView.openWorkStoppageInPlanning).
     */
    function openLeaveInPlanning(leave) {
        if (!leave.fromDate) return
        const idTeam = leave.id_team || selectedTeamsIds[0]
        if (leave.id_team) dispatch(setSelectedTeams([leave.id_team]))
        navigate(`/planning/${idTeam}/WEEKLY${moment(leave.fromDate).format("DDMMYYYY")}`)
    }

    /**
     * The leave view reads the planning's shape: `collab` is the collab's name, and `createdAt`
     * a raw date it formats itself. A row of the Congés list carries the name as prenom / nom
     * and `createdAt` already formatted for display — formatting that again gave "Invalid date"
     * for any locale moment could not parse back — so both are mapped here.
     */
    const openLeaveDetails = (leave) => dispatch(showLeaveModal({
        type: "UPDATE_LEAVE",
        data: {
            ...leave,
            collab: leave.collab || `${leave.prenom || ""} ${leave.nom || ""}`.trim(),
            createdAt: leave.createdAtTime || null,
        }
    }))

    // The requests this viewer can answer, the same rule as the Validate / Reject actions.
    const pendingLeaves = leaves2.filter(leave => leave.status === 'PENDING' && canManage(leave))

    const pendingPanel = (
        <LeavePendingRequests
            requests={pendingLeaves}
            onValidate={(leave) => updateLeave(leave.id, "VALIDATED")}
            onReject={(leave) => updateLeave(leave.id, "REJECTED")}
            onOpen={openLeaveDetails}
            sx={{ mb: 1.5 }}
        />
    )

    // Same buttons as Work stoppages: a manager gets HR's orange add icon (as for declaring an
    // arrêt), a collab the labelled "Déclarer un arrêt"-style button.
    const requestLeaveButton = hasEditOnViewedTeams ? (
        <ImageButton
            image={addIcon}
            imageFocus={addIconFocus}
            onClick={openNew}
            active={true}
            tooltip={<Langs str="Request Leave" />}
            width={32} />
    ) : (
        <Button
            variant="contained"
            color="warning"
            size="small"
            className="me-2"
            startIcon={<AddCircleOutlineIcon />}
            onClick={openNew}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
        >
            <Langs str="Request Leave" />
        </Button>
    )

    const toolBar = () => {
        let canExport = false
        selectedTeamsIds.map(id => {
            if (isManager(id)) {
                canExport = true
            }
        })
        if (isMobile == 1) {
            return (
                <Row>
                    <Col lg={6} xs={12} className='headerTitle'>
                        <span className="headerTitle">
                            <Langs str="Leaves" />
                        </span>
                        <TeamSelector includeMyTeam />
                    </Col>
                    <Col lg={6} xs={12} className='d-flex justify-content-end pe-4'>
                        {canRequestLeave && requestLeaveButton}

                        {canExport && <ImageButton
                            image={exportIcon}
                            imageFocus={exportIconFocus}
                            onClick={exportExcel}
                            active={true}
                            tooltip="Export"
                            width={32} />}

                        {hasEditOnViewedTeams && (
                            <Button
                                variant="outlined"
                                size="small"
                                className="ms-2"
                                onClick={() => dispatch(showAdjustmentModal())}
                                sx={{
                                    color: '#FF9015',
                                    borderColor: '#FF9015',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: '#FF9015',
                                        backgroundColor: 'rgba(255, 144, 21, 0.08)',
                                    },
                                }}
                            >
                                <Langs str="LEAVE_ADJUSTMENTS" />
                            </Button>
                        )}
                    </Col>
                </Row>
            )
        }
        if (isMobile == 2) {
            return (
                <MobileLeavesTopMenu
                    canExport={canExport}
                    canRequestLeave={canRequestLeave}
                    onAddLeave={openNew}
                    onExport={exportExcel}
                    canAdjust={hasEditOnViewedTeams}
                    onAdjust={() => dispatch(showAdjustmentModal())}
                />
            )
        }
        if (isMobile == 3) {
            return (
                <Row className="align-items-center">
                    <Col md={2} className="d-flex align-items-center">
                        <span className="headerTitle">
                            <Langs str="Leaves" />
                        </span>
                    </Col>
                    <Col md={6} className="d-flex align-items-center">
                        <TeamSelector includeMyTeam />
                    </Col>
                    <Col md={4} className="d-flex justify-content-end align-items-center pe-4">
                        {canRequestLeave && requestLeaveButton}
                        {canExport && (
                            <ImageButton
                                image={exportIcon}
                                imageFocus={exportIconFocus}
                                onClick={exportExcel}
                                active={true}
                                tooltip="Export"
                                width={32}
                            />
                        )}
                        {hasEditOnViewedTeams && (
                            <Button
                                variant="outlined"
                                size="small"
                                className="ms-2"
                                onClick={() => dispatch(showAdjustmentModal())}
                                sx={{
                                    color: '#FF9015',
                                    borderColor: '#FF9015',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: '#FF9015',
                                        backgroundColor: 'rgba(255, 144, 21, 0.08)',
                                    },
                                }}
                            >
                                <Langs str="LEAVE_ADJUSTMENTS" />
                            </Button>
                        )}
                    </Col>
                </Row>
            )
        }
    }

    return (
        <div>
            <div className="topMenu">
                {toolBar()}
            </div>

            {isMobile === 2 ? (
                <>
                    <MobileLeavesView
                        leaves={leaves2}
                        userId={userId}
                        userRoleId={userRoleId}
                        myTeam={myTeam}
                        onValidate={(id) => updateLeave(id, "VALIDATED")}
                        onReject={(id) => updateLeave(id, "REJECTED")}
                        onDelete={handleDelete}
                        onRequestDelete={handleRequestDelete}
                        onOpenPlanning={openLeaveInPlanning}
                        header={pendingPanel}
                    />

                    <MobileLeaveFormDrawer
                        show={leaveModalType}
                        onHide={() => dispatch(hideLeaveModal())}
                    />
                </>
            ) : (
                // Clears the fixed top bar once, for the pending panel and the table together,
                // as WorkStoppagesView does.
                <Box sx={{ pt: isMobile === 3 ? "95px" : "75px" }}>
                    {pendingLeaves.length ? <Box sx={{ mx: "15px" }}>{pendingPanel}</Box> : null}
                    <LeavesDataTable
                        apiRef={dtLeavesApi}
                        data={leaves2}
                        sampleLeaves={sampleLeaves}
                        // Leaves room for the collapsed pending panel (~56px) when it is shown.
                        scrollHeight={`calc(100vh - ${(isMobile === 3 ? 190 : 170) + (pendingLeaves.length ? 56 : 0)}px)`}
                        canManage={canManage}
                        userId={userId}
                        onViewDetails={openLeaveDetails}
                        onOpenPlanning={openLeaveInPlanning}
                        onValidate={(leave) => updateLeave(leave.id, "VALIDATED")}
                        onReject={(leave) => updateLeave(leave.id, "REJECTED")}
                        onDelete={handleDelete}
                        onRequestDelete={handleRequestDelete}
                    />
                    <LeaveFormModal show={leaveModalType} onHide={() => dispatch(hideLeaveModal())} />
                </Box>
            )}

            <ConfirmDeleteModal
                show={showDeletePopup}
                title={<Langs str='DELETE_LEAVE' />}
                message={<Langs str='DELETE_LEAVE_MSG' />}
                onDelete={handleDeleteSelectedLeave}
                onHide={() => setShowDeletePopup(false)} />

            <ConfirmModal show={showConfirmPopup}
                title={<Langs str='DELETE_LEAVE' />}
                message={<Langs str={selectedLeave.status == 'PENDING' ? 'DELETE_LEAVE_MSG' : 'REQUEST_DELETE_LEAVE_MSG'} />}
                onConfirm={handleRequestDeleteSelectedLeave}
                onHide={() => setShowConfirmPopup(false)} />

            <LeaveAdjustmentsModal
                show={adjustmentModalOpen}
                onHide={() => dispatch(hideAdjustmentModal())} />
        </div>
    )
}
